import './CollectionsModal.scss'

import { HDSButton, HDSCheckbox } from '@here/hds-react-components'
import { signIn } from 'components/Account/data/reducer'
import { ScrollableContainer } from 'components/App/components/ScrollableContainer'
import { Collection } from 'components/Collections/data/model'
import {
  deletePlaceFromCollection,
  savePlaceInCollection,
} from 'components/Collections/data/reducer'
import { PdcStateItem } from 'data/model/pdc'
import { RootState } from 'data/reducer'
import { useAppDispatch, useAppSelector } from 'data/store/hooks'
import { i18n } from 'lib/i18n'
import { deserializePlaceV0 } from 'lib/place'
import React, { RefObject, useEffect, useMemo, useRef } from 'react'
import { useLocation } from 'react-router-dom'

interface CollectionsModalProps {
  position: {
    left: number
    top: number
  }
  onClose: () => void
  pdcItem: PdcStateItem
}
export const CollectionsModal = ({
  onClose,
  position,
  pdcItem,
}: CollectionsModalProps): JSX.Element => {
  const dispatch = useAppDispatch()
  const loggedOutLabel = i18n('collections.modal.sign-in.button')
  const collectionsMap = useAppSelector((state) => state.collections.collectionMap)
  const collections = Object.values(collectionsMap)
  const hasCollections = collections.length > 0
  const placeMaps = useAppSelector(({ collections }) => collections.placeMap)
  const { hash, pathname, search } = useLocation()
  const locationState = useMemo(
    () => ({ hash, pathName: pathname, search }),
    [hash, pathname, search]
  )
  const isUserLoggedIn: boolean = useAppSelector(
    (state: RootState) => state.account.user !== undefined
  )
  const loggedInLabel = hasCollections
    ? i18n('collections.collections-card.actions.new-collection')
    : i18n('collections.modal.create-collection.button')
  const titleLabel = isUserLoggedIn
    ? hasCollections
      ? i18n('collections.modal.add.title')
      : i18n('collections.modal.start.title')
    : i18n('collections.modal.add.title')

  const handleOnChange = (collectionId: string): void => {
    if (pdcItem.id !== undefined) {
      if (isPlaceSavedInCollection(collectionId)) {
        dispatch(deletePlaceFromCollection(pdcItem.id, collectionId))
        onClose()
      } else {
        dispatch(savePlaceInCollection(pdcItem, collectionId))
        onClose()
      }
    }
  }

  const handleOnClick = (): void => {
    dispatch(signIn({ ...locationState, action: 'sign-in' }))
  }

  const isPlaceSavedInCollection = (id: string): boolean => {
    let isSaved = false

    Object.values(placeMaps).forEach((p) => {
      const deserializedPlace = deserializePlaceV0(p.idV0)

      if (deserializedPlace.id === pdcItem.id || deserializedPlace.placesForeignId === pdcItem.id) {
        if (p.collectionId.includes(id)) {
          isSaved = true
        }
      }
    })

    return isSaved
  }

  return (
    <CollectionsModalWrapper onClose={onClose}>
      <div
        data-testid="CollectionsModal"
        className="CollectionsModal"
        style={
          {
            left: `${position.left}px`,
            top: `calc(${position.top}px + 2.5rem)`,
          } as React.CSSProperties
        }
      >
        <ScrollableContainer>
          <>
            <div className="CollectionsModal__title" data-testid="CollectionsModal__title">
              {titleLabel}
            </div>
            {isUserLoggedIn ? (
              <>
                {hasCollections ? (
                  <div className="CollectionsModal__list">
                    {Object.values(collectionsMap).map((collection: Collection) => (
                      <HDSCheckbox
                        key={collection.id}
                        onChange={() => {
                          handleOnChange(collection.id)
                        }}
                        checked={isPlaceSavedInCollection(collection.id)}
                        data-testid="CollectionsModal__item"
                      >
                        <span
                          className="CollectionsModal__item"
                          data-testid="CollectionsModal__item-name"
                        >
                          {collection.name}
                        </span>
                      </HDSCheckbox>
                    ))}
                  </div>
                ) : (
                  <div className="CollectionsModal__prompt" data-testid="CollectionsModal__prompt">
                    <div>{i18n('collections.modal.empty.description')}</div>
                    <div>{i18n('collections.modal.empty.prompt')}</div>
                  </div>
                )}
              </>
            ) : (
              <div className="CollectionsModal__prompt" data-testid="CollectionsModal__prompt">
                {i18n('collections.modal.sign-in.description')}
              </div>
            )}
          </>
        </ScrollableContainer>
        <HDSButton
          disabled={isUserLoggedIn}
          variant="link"
          icon={isUserLoggedIn ? 'add' : 'sign-in'}
          className="CollectionsModal__button"
          onClick={handleOnClick}
          data-testid="CollectionsModal__button"
        >
          {isUserLoggedIn ? loggedInLabel : loggedOutLabel}
        </HDSButton>
      </div>
    </CollectionsModalWrapper>
  )
}

interface CollectionsModalWrapper extends React.PropsWithChildren {
  onClose: () => void
}
const CollectionsModalWrapper = ({ children, onClose }: CollectionsModalWrapper): JSX.Element => {
  const wrapperRef = useRef(null)

  const useOutsideAlerter = (ref: RefObject<HTMLDivElement>): void => {
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent): void => {
        if (event.target && ref.current && !ref.current.contains(event.target as Node)) {
          onClose()
        }
      }

      window.addEventListener('mousedown', handleClickOutside)

      return () => {
        window.removeEventListener('mousedown', handleClickOutside)
      }
    }, [ref])
  }

  useOutsideAlerter(wrapperRef)

  return <div ref={wrapperRef}>{children}</div>
}
